/**
 * The instrument's control brain: hands in, frequency, gain and vibrato out.
 *
 * Responsibilities kept deliberately narrow — it does not touch the DOM, Three.js or Web
 * Audio, so the whole control model can be driven from synthetic landmark sequences in unit
 * tests.
 *
 * Role assignment is the part that makes or breaks the instrument in practice, because
 * nothing is more disruptive mid-note than pitch and volume trading places. Three rules,
 * in order:
 *
 *  1. **Sticky.** A hand keeps the role it was given for as long as the tracker keeps its
 *     identity, so crossing the hands or briefly losing one changes nothing.
 *  2. **Side, on re-acquisition.** When a hand appears with no history, it takes the role
 *     belonging to the side of the instrument it is on, rather than whichever antenna it
 *     happens to be nearest. Walking out of frame and back gives the same answer every time.
 *  3. **A slow safety net.** If both hands are visibly on the wrong sides, and stay that way
 *     for the best part of a second, the roles are exchanged. That recovers from a genuine
 *     tracking mix-up without ever flickering: brief crossings never last long enough.
 */

import type { Point2, TrackedHand } from '../tracking/types';
import { clamp01 } from '../tracking/types';
import type { Settings } from '../app/settings';
import {
  distanceToPitchNormalized,
  frequencyToNote,
  normalizedToFrequency,
} from '../audio/pitchMapping';
import type { NoteReading } from '../audio/pitchMapping';
import { distanceToVolumeNormalized, normalizedToGain } from '../audio/volumeMapping';

export type ControlRole = 'pitch' | 'volume';

export interface ControlTimings {
  /** Hold the last value for this long after a hand disappears. */
  holdMs: number;
  /** Then fade the control's contribution to silence over this long. */
  fadeMs: number;
  /**
   * Fade back in over this long when the hand returns. Long enough that no single frame is a
   * large jump in amplitude, short enough to feel immediate when a hand re-enters frame.
   */
  attackMs: number;
}

export const DEFAULT_CONTROL_TIMINGS: ControlTimings = {
  holdMs: 160,
  fadeMs: 260,
  attackMs: 130,
};

/**
 * How far apart, along the instrument's own axis, the two hands have to be on the wrong
 * sides before the safety net starts counting, and for how long.
 *
 * 0.12 screen units is roughly a palm's width, so ordinary hand-to-hand jitter never trips
 * it. 900 ms is comfortably longer than any crossing gesture but short enough that a player
 * who genuinely swapped hands is not left fighting the instrument.
 */
export const DEFAULT_REANCHOR = {
  margin: 0.12,
  holdMs: 900,
} as const;

/** Fixed vibrato depths for the non-gestural presets, as a fraction of the configured depth. */
const PRESET_VIBRATO: Readonly<Record<string, number>> = {
  off: 0,
  gentle: 0.42,
  rich: 1,
};

export interface ControlOutput {
  frequency: number;
  /** Post-curve, post-master linear gain ready for the audio engine. */
  gain: number;
  pitchNormalized: number;
  volumeNormalized: number;
  /** Raw distances in screen units, or null when the hand is absent. Used by calibration. */
  pitchDistance: number | null;
  volumeDistance: number | null;
  pitchHandId: string | null;
  volumeHandId: string | null;
  /** True while the control has a live or held hand (i.e. before the fade completes). */
  pitchPresent: boolean;
  volumePresent: boolean;
  handCount: number;
  note: NoteReading;
  /** 0..1 vibrato depth scaler for the audio engine, after the mode has been applied. */
  vibratoAmount: number;
  /** How open the pitch hand is, 0..1, held through dropouts. Drives gestural vibrato. */
  pitchOpenness: number;
  /** True on the single frame the safety net exchanged the two roles. */
  rolesReanchored: boolean;
}

/** Per-role presence tracking and hold/fade envelope. */
class RoleState {
  handId: string | null = null;

  lastSeenMs = 0;

  /** Last good normalized control value; held through dropouts. */
  value = 0;

  /** Last good hand openness; held through dropouts so vibrato does not drop out with it. */
  openness = 0;

  /** Last good distance, for calibration and visuals. */
  distance: number | null = null;

  /** 0..1 envelope; 1 while the hand is present, ramping down after the grace period. */
  presence = 0;

  seenOnce = false;

  reset(): void {
    this.handId = null;
    this.lastSeenMs = 0;
    this.value = 0;
    this.openness = 0;
    this.distance = null;
    this.presence = 0;
    this.seenOnce = false;
  }
}

export interface ThereminControllerOptions {
  timings?: Partial<ControlTimings>;
  reanchor?: Partial<typeof DEFAULT_REANCHOR>;
}

export class ThereminController {
  private readonly timings: ControlTimings;

  private readonly reanchor: { margin: number; holdMs: number };

  private readonly pitch = new RoleState();

  private readonly volume = new RoleState();

  /** Assignment by instrument side, before the user's swap preference is applied. */
  private slotNearPitch: string | null = null;

  private slotNearVolume: string | null = null;

  private readonly pitchAnchor: Point2 = { x: 0.32, y: 0.12 };

  private readonly volumeAnchor: Point2 = { x: -0.32, y: -0.04 };

  /** Unit vector from the volume anchor towards the pitch anchor, and their midpoint. */
  private axisX = 1;

  private axisY = 0;

  private midX = 0;

  private midY = 0;

  /** How long the two hands have been convincingly on the wrong sides, in ms. */
  private reanchorPressureMs = 0;

  private lastUpdateMs = 0;

  /** Reused so the per-frame assignment never allocates. */
  private readonly freeHands: TrackedHand[] = [];

  private readonly output: ControlOutput = {
    frequency: 220,
    gain: 0,
    pitchNormalized: 0,
    volumeNormalized: 0,
    pitchDistance: null,
    volumeDistance: null,
    pitchHandId: null,
    volumeHandId: null,
    pitchPresent: false,
    volumePresent: false,
    handCount: 0,
    note: frequencyToNote(220),
    vibratoAmount: 0,
    pitchOpenness: 0,
    rolesReanchored: false,
  };

  constructor(options: ThereminControllerOptions = {}) {
    this.timings = { ...DEFAULT_CONTROL_TIMINGS, ...options.timings };
    this.reanchor = { ...DEFAULT_REANCHOR, ...options.reanchor };
    this.refreshAxis();
  }

  /** Screen-space positions of the two interaction anchors, refreshed by the scene. */
  setAnchors(pitch: Point2, volume: Point2): void {
    this.pitchAnchor.x = pitch.x;
    this.pitchAnchor.y = pitch.y;
    this.volumeAnchor.x = volume.x;
    this.volumeAnchor.y = volume.y;
    this.refreshAxis();
  }

  get anchors(): { pitch: Readonly<Point2>; volume: Readonly<Point2> } {
    return { pitch: this.pitchAnchor, volume: this.volumeAnchor };
  }

  /** Drop all assignments (used when the camera flips or the user resets). */
  reset(): void {
    this.pitch.reset();
    this.volume.reset();
    this.slotNearPitch = null;
    this.slotNearVolume = null;
    this.reanchorPressureMs = 0;
  }

  get state(): Readonly<ControlOutput> {
    return this.output;
  }

  /**
   * Advance the control model by one frame.
   *
   * @param hands    identity-stable tracked hands (screen space)
   * @param settings live settings
   * @param nowMs    `performance.now()`
   */
  update(
    hands: readonly TrackedHand[],
    settings: Readonly<Settings>,
    nowMs: number,
  ): Readonly<ControlOutput> {
    const dtMs =
      this.lastUpdateMs === 0 ? 16 : Math.min(250, Math.max(0, nowMs - this.lastUpdateMs));
    this.lastUpdateMs = nowMs;

    const reanchored = this.assignSlots(hands, settings, dtMs);

    const pitchHand = this.findHand(hands, this.pitch.handId);
    const volumeHand = this.findHand(hands, this.volume.handId);

    this.updateRole(this.pitch, pitchHand, 'pitch', settings, nowMs, dtMs);
    this.updateRole(this.volume, volumeHand, 'volume', settings, nowMs, dtMs);

    const frequency = normalizedToFrequency(this.pitch.value, settings);
    // Both envelopes gate the output: with no pitch hand the instrument has no meaningful
    // note, so it fades out rather than droning on the last frequency.
    //
    // The envelope multiplies the *output* gain rather than the pre-curve control value, so a
    // fade is linear in amplitude. Folding it in before the squaring curve would make the
    // fade quartic in time when both hands are lost together — slow, then abrupt.
    const envelope = this.pitch.presence * this.volume.presence;
    const gain = normalizedToGain(this.volume.value, settings.masterVolume) * envelope;

    const out = this.output;
    out.frequency = frequency;
    out.gain = settings.muted ? 0 : gain;
    out.pitchNormalized = this.pitch.value;
    out.volumeNormalized = this.volume.value * envelope;
    out.pitchDistance = this.pitch.distance;
    out.volumeDistance = this.volume.distance;
    out.pitchHandId = this.pitch.handId;
    out.volumeHandId = this.volume.handId;
    out.pitchPresent = this.pitch.presence > 0.001;
    out.volumePresent = this.volume.presence > 0.001;
    out.handCount = hands.reduce((count, hand) => count + (hand.visible ? 1 : 0), 0);
    out.note = frequencyToNote(frequency);
    out.pitchOpenness = this.pitch.openness;
    out.vibratoAmount = vibratoAmountFor(settings, this.pitch.openness);
    out.rolesReanchored = reanchored;
    return out;
  }

  private findHand(hands: readonly TrackedHand[], id: string | null): TrackedHand | null {
    if (!id) return null;
    for (const hand of hands) {
      if (hand.id === id) return hand;
    }
    return null;
  }

  private refreshAxis(): void {
    const dx = this.pitchAnchor.x - this.volumeAnchor.x;
    const dy = this.pitchAnchor.y - this.volumeAnchor.y;
    const length = Math.hypot(dx, dy);
    if (length < 1e-4) {
      this.axisX = 1;
      this.axisY = 0;
    } else {
      this.axisX = dx / length;
      this.axisY = dy / length;
    }
    this.midX = (this.pitchAnchor.x + this.volumeAnchor.x) / 2;
    this.midY = (this.pitchAnchor.y + this.volumeAnchor.y) / 2;
  }

  /**
   * Where a hand sits along the instrument's own axis, measured from the midpoint between
   * the two anchors. Positive is the pitch side.
   *
   * Projecting onto the axis rather than comparing raw distances is what makes this stable:
   * a hand held far away from *both* antennas still has an unambiguous side, whereas "which
   * antenna is nearer" flips on tiny movements when the hand is roughly equidistant.
   */
  private sideOf(hand: TrackedHand, settings: Readonly<Settings>): number {
    const point = this.controlPointOf(hand, settings);
    return (point.x - this.midX) * this.axisX + (point.y - this.midY) * this.axisY;
  }

  /**
   * Keep `slotNearPitch` / `slotNearVolume` populated, then project them onto the two roles
   * according to the swap preference.
   *
   * @returns true when the safety net exchanged the two slots on this frame
   */
  private assignSlots(
    hands: readonly TrackedHand[],
    settings: Readonly<Settings>,
    dtMs: number,
  ): boolean {
    const live = new Set<string>();
    for (const hand of hands) live.add(hand.id);
    if (this.slotNearPitch && !live.has(this.slotNearPitch)) this.slotNearPitch = null;
    if (this.slotNearVolume && !live.has(this.slotNearVolume)) this.slotNearVolume = null;

    const free = this.freeHands;
    free.length = 0;
    for (const hand of hands) {
      if (!hand.visible) continue;
      if (hand.id === this.slotNearPitch || hand.id === this.slotNearVolume) continue;
      free.push(hand);
    }
    // Most-pitch-side hand first. At most two entries, so this is a single comparison.
    if (free.length === 2) {
      free.sort((a, b) => this.sideOf(b, settings) - this.sideOf(a, settings));
    }

    if (free.length >= 2 && !this.slotNearPitch && !this.slotNearVolume) {
      this.slotNearPitch = free[0]!.id;
      this.slotNearVolume = free[1]!.id;
    } else {
      for (const hand of free) {
        if (!this.slotNearPitch && !this.slotNearVolume) {
          // A lone hand with no history: its side of the instrument decides.
          if (this.sideOf(hand, settings) >= 0) this.slotNearPitch = hand.id;
          else this.slotNearVolume = hand.id;
        } else if (!this.slotNearPitch) {
          this.slotNearPitch = hand.id;
        } else if (!this.slotNearVolume) {
          this.slotNearVolume = hand.id;
        }
      }
    }

    const reanchored = this.updateReanchor(hands, settings, dtMs);

    this.pitch.handId = settings.swapHands ? this.slotNearVolume : this.slotNearPitch;
    this.volume.handId = settings.swapHands ? this.slotNearPitch : this.slotNearVolume;
    return reanchored;
  }

  /** The slow safety net; see the class comment. */
  private updateReanchor(
    hands: readonly TrackedHand[],
    settings: Readonly<Settings>,
    dtMs: number,
  ): boolean {
    const pitchHand = this.findHand(hands, this.slotNearPitch);
    const volumeHand = this.findHand(hands, this.slotNearVolume);
    if (!pitchHand?.visible || !volumeHand?.visible) {
      this.reanchorPressureMs = 0;
      return false;
    }

    const margin = this.sideOf(volumeHand, settings) - this.sideOf(pitchHand, settings);
    if (margin <= this.reanchor.margin) {
      // Decay twice as fast as it builds, so a hand that wanders across and back leaves no
      // residue behind it.
      this.reanchorPressureMs = Math.max(0, this.reanchorPressureMs - dtMs * 2);
      return false;
    }

    this.reanchorPressureMs += dtMs;
    if (this.reanchorPressureMs < this.reanchor.holdMs) return false;

    this.reanchorPressureMs = 0;
    const previousPitch = this.slotNearPitch;
    this.slotNearPitch = this.slotNearVolume;
    this.slotNearVolume = previousPitch;
    return true;
  }

  private controlPointOf(hand: TrackedHand, settings: Readonly<Settings>): Point2 {
    return settings.controlPoint === 'indexTip' ? hand.indexTip : hand.palm;
  }

  private distanceTo(hand: TrackedHand, anchor: Point2, settings: Readonly<Settings>): number {
    const point = this.controlPointOf(hand, settings);
    return Math.hypot(point.x - anchor.x, point.y - anchor.y);
  }

  /** Screen-space control point for a role, or null when that role has no hand. */
  controlPointFor(
    role: ControlRole,
    hands: readonly TrackedHand[],
    settings: Readonly<Settings>,
  ): Point2 | null {
    const hand = this.handFor(role, hands);
    if (!hand) return null;
    return this.controlPointOf(hand, settings);
  }

  /** The visible hand currently driving a role, if any. */
  handFor(role: ControlRole, hands: readonly TrackedHand[]): TrackedHand | null {
    const id = role === 'pitch' ? this.pitch.handId : this.volume.handId;
    const hand = this.findHand(hands, id);
    return hand && hand.visible ? hand : null;
  }

  private updateRole(
    state: RoleState,
    hand: TrackedHand | null,
    role: ControlRole,
    settings: Readonly<Settings>,
    nowMs: number,
    dtMs: number,
  ): void {
    const anchor = role === 'pitch' ? this.pitchAnchor : this.volumeAnchor;
    const range = role === 'pitch' ? settings.pitchRange : settings.volumeRange;
    const invert = role === 'pitch' ? settings.invertPitch : settings.invertVolume;

    if (hand && hand.visible) {
      const distance = this.distanceTo(hand, anchor, settings);
      const options = {
        near: range.near,
        far: range.far,
        sensitivity: settings.sensitivity,
        invert,
      };
      state.value =
        role === 'pitch'
          ? distanceToPitchNormalized(distance, options)
          : distanceToVolumeNormalized(distance, options);
      state.distance = distance;
      state.openness = clamp01(hand.openness);
      state.lastSeenMs = nowMs;
      state.seenOnce = true;
      // Ramp in rather than snapping, so re-acquiring a hand never clicks.
      state.presence = clamp01(state.presence + dtMs / Math.max(1, this.timings.attackMs));
      return;
    }

    state.distance = null;
    if (!state.seenOnce) {
      state.presence = 0;
      return;
    }

    const elapsed = nowMs - state.lastSeenMs;
    if (elapsed <= this.timings.holdMs) {
      // Grace period: hold the last value untouched.
      return;
    }
    const fadeElapsed = elapsed - this.timings.holdMs;
    state.presence = clamp01(1 - fadeElapsed / Math.max(1, this.timings.fadeMs));
    if (state.presence <= 0) {
      // Safe state. The stored value is kept so a returning hand resumes smoothly rather
      // than jumping from zero.
      state.presence = 0;
    }
  }
}

/**
 * Vibrato depth scaler for the current mode.
 *
 * `hand` is the expressive one: opening the pitch hand widens the vibrato and closing it into
 * a fist removes it entirely, which is how a thereminist actually shapes a note. The fixed
 * presets exist because that takes practice, and a first-time player should still get a voice
 * that sounds like an instrument rather than a test tone.
 */
function vibratoAmountFor(settings: Readonly<Settings>, openness: number): number {
  if (settings.vibratoMode === 'hand') return clamp01(openness);
  return PRESET_VIBRATO[settings.vibratoMode] ?? 0;
}
